<template>
	<div class="main-services-section relative flex flex-col px-6 pt-10">
		<LandingShape10 class="absolute end-0 top-0 mt-6 h-auto w-[269px] ltr:-scale-x-100" />
		<LandingShape1 class="absolute bottom-0 start-0 mb-4 ms-8 h-auto w-[78px]" />
		<LandingShape2 class="absolute bottom-0 end-0 top-[40%] my-auto me-[72px] h-auto w-[32px]" />
		<LandingShape4 class="absolute bottom-0 start-0 mb-[90px] ms-6 h-auto w-[44px] text-[#F3CDB6]" />
		<div class="relative flex w-full max-w-screen-lg flex-col self-center">
			<span class="text-center text-3xl font-black text-[#382C5C] md:text-4xl">
				{{ $t("our_services") }}
			</span>
			<span class="mt-3 text-center text-base text-[#686F7A] md:text-lg">
				{{ $t("our_services_subtitle", { n: items.length }, items.length) }}
			</span>
			<div class="-mx-6 overflow-hidden px-6 pb-28 pt-16">
				<Swiper
					class="w-full !overflow-visible"
					:pagination="{
						enabled: true
					}"
					:breakpoints="{
						0: { slidesPerView: 1, spaceBetween: 24 },
						640: { slidesPerView: 2, spaceBetween: 24 },
						768: { slidesPerView: 2, spaceBetween: 24 },
						1024: { slidesPerView: 3, spaceBetween: 24 },
						1280: { slidesPerView: 3, spaceBetween: 44 }
					}"
					:modules="[Pagination]"
				>
					<SwiperSlide v-for="(item, index) in items" :key="item.id">
						<div
							class="service-card group bg-white flex flex-col items-center rounded-2xl px-8 pt-9 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
							:class="`stagger-${index}`"
							:style="{
								boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)'
							}"
						>
							<span
								class="service-icon relative h-[82px] w-[89px] transition-transform duration-300 group-hover:scale-110"
							>
								<LandingServiceShape1
									:class="[
										'absolute left-0 top-0 h-full w-full opacity-20 transition-opacity duration-300 group-hover:opacity-30'
									]"
									:style="{
										color: item.color
									}"
								/>
								<img
									:src="item.image"
									alt=""
									class="relative h-full w-full object-contain transition-transform duration-300 group-hover:rotate-6"
								/>
							</span>
							<span
								class="mt-5 text-lg font-bold text-[#382C5C] transition-colors duration-300"
								:class="`group-hover:text-[${item.color}]`"
							>
								{{ item.title }}
							</span>
							<span class="mt-4 text-sm text-[#686F7A]">
								{{ item.description }}
							</span>
							<span
								class="mt-9 h-5 w-20 rounded-t-full transition-all duration-300 group-hover:w-full"
								:style="{ backgroundColor: item.color }"
							></span>
						</div>
					</SwiperSlide>
				</Swiper>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core"
import { Pagination } from "swiper/modules"
import LandingServiceShape1 from "~/assets/svg/landing_service_shape_1.svg"
import LandingShape10 from "~/assets/svg/landing_shape_10.svg"

import LandingShape1 from "~/assets/svg/landing_shape_1.svg"
import LandingShape2 from "~/assets/svg/landing_shape_2.svg"
import LandingShape4 from "~/assets/svg/landing_shape_4.svg"

const app = useNuxtApp()

onMounted(() => {
	const cards = document.querySelectorAll<HTMLElement>(".service-card")

	cards.forEach((card) => {
		useIntersectionObserver(
			card as HTMLElement,
			([{ isIntersecting }]) => {
				if (isIntersecting) {
					card.classList.add("animate-in")
				}
			},
			{
				threshold: 0.1,
				rootMargin: "0px 0px -50px 0px"
			}
		)
	})
})

const items = computed(() => [
	{
		id: "payroll",
		title: app.$i18n.t("landing_services_1_title"),
		description: app.$i18n.t("landing_services_1_description"),
		image: "/img/landing_services_1.png",
		color: "#EA6939"
	},
	{
		id: "job-request",
		title: app.$i18n.t("landing_services_2_title"),
		description: app.$i18n.t("landing_services_2_description"),
		image: "/img/landing_services_2.png",
		color: "#3A86EA"
	},
	{
		id: "performance-review",
		title: app.$i18n.t("landing_services_3_title"),
		description: app.$i18n.t("landing_services_3_description"),
		image: "/img/landing_services_3.png",
		color: "#39EAB5"
	},
	{
		id: "custom",
		title: app.$i18n.t("landing_services_4_title"),
		description: app.$i18n.t("landing_services_4_description"),
		image: "/img/landing_services_4.png",
		color: "#EA3A86"
	}
])
</script>

<style>
.main-services-section .swiper-pagination-horizontal {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: -72px;
}
.main-services-section .swiper-pagination-bullet {
	@apply bg-[#babdb8] opacity-100 transition-all duration-300;
}
.main-services-section .swiper-pagination-bullet-active {
	@apply bg-primary opacity-100;
	transform: scale(1.2);
}

/* Service card animations */
.service-card {
	opacity: 0;
	transform: translateY(40px);
	transition:
		opacity 0.6s ease-out,
		transform 0.6s ease-out,
		box-shadow 0.3s ease,
		translate 0.3s ease;
}

.service-card.animate-in {
	opacity: 1;
	transform: translateY(0);
}

.service-card.stagger-0 {
	transition-delay: 0s;
}

.service-card.stagger-1 {
	transition-delay: 0.1s;
}

.service-card.stagger-2 {
	transition-delay: 0.2s;
}

.service-card.stagger-3 {
	transition-delay: 0.3s;
}

/* Icon hover animation */
.service-icon img {
	filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
}

.service-card:hover .service-icon img {
	filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.2));
}
</style>
